import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { BlackboardStreamService } from "../../../../services/blackboard-stream.service";
import type {
  BlackboardEnginePassState,
  BlackboardTaskAttemptInfo,
} from "../../../../services/blackboard-stream.service";
import { PlanBlockStoreService } from "../../../../services/plan-block-store.service";
import { PLAN_PHASE_INFO } from "../plan-phase-descriptions";
import { PlanStepListComponent } from "../plan-shared/plan-step-list/plan-step-list.component";
import type { PlanStepListStep } from "../plan-shared/plan-step-list/plan-step-list.component";
import { frontendToolTaskIds } from "../plan-shared/plan-task-status";
import type { PlanTaskState } from "../plan-shared/plan-task-status";
import { HitlService } from "../../../../services/hitl.service";
import type { BlackboardIndicator, BlackboardPhase, BlackboardTaskRow } from "../../../../model/block.types";
import type { PlanData, PlanStep } from "../../../../model/plan.types";
import type { TaskExecution } from "../../../../model/engine-run-state.types";

/** Empty-signal fallbacks shared by the optional stream reads — stable identities, no churn. */
const EMPTY_INDICATORS: Partial<Record<BlackboardPhase, BlackboardIndicator>> = {};
const EMPTY_INTENT_MAP: ReadonlyMap<string, string> = new Map();
const EMPTY_PASS_MAP: ReadonlyMap<string, BlackboardEnginePassState> = new Map();
const EMPTY_ATTEMPT_MAP: ReadonlyMap<string, BlackboardTaskAttemptInfo> = new Map();

/** Fixed display order of the doc 31 §2.3 phase catalog — matches the backend pipeline order. */
const PHASE_ORDER: readonly BlackboardPhase[] = [
  "plan_router",
  "semantic_decomposition",
  "capability_resolution",
  "provider_binding",
  "plan_triage",
  "plan_assessment",
  "plan_run",
  "run_engine",
];

/**
 * Resolved display state of one phase row of the vertical rail (plan-cards-spec §Plan progress
 * 2). Three spec states plus `failed` and `waiting`: the live stream carries an `error` status
 * and a durable `run_engine` failure record (collapsing either into idle/completed would hide
 * the failure), and the status contract v3 parks the engine when the board is waiting — the
 * row must pause instead of spinning indefinitely.
 */
type PhaseRowState = "completed" | "running" | "failed" | "waiting" | "idle";

/** Right-rail copy per phase state: icon ligature, tone class and the tooltip state word. */
const PHASE_RAIL_COPY: Readonly<Record<PhaseRowState, { icon: string; toneClass: string; word: string }>> = {
  completed: { icon: "check_circle", toneClass: "tone-ok", word: "completed" },
  running: { icon: "sync", toneClass: "tone-pri", word: "running" },
  failed: { icon: "error", toneClass: "tone-err", word: "failed" },
  waiting: { icon: "pause_circle", toneClass: "tone-warn", word: "waiting" },
  idle: { icon: "radio_button_unchecked", toneClass: "tone-idle", word: "idle" },
};

/** Display labels of the known engine passes; unknown or null passes render the neutral fallback. */
const ENGINE_PASS_LABELS: Readonly<Record<string, string>> = {
  discovery: "Discovery",
  execution: "Execution",
  analysis: "Analysis",
};

/** Canonical display order of the known engine passes — unknown passes and the fallback follow. */
const ENGINE_PASS_ORDER: readonly string[] = ["discovery", "execution", "analysis"];

/** Resolved render model of one engine-pass sub-row (nested under the `run_engine` phase row). */
interface EnginePassRowView {
  /** Stream map key — stable Angular `@for` track identity across pass-state updates. */
  key: string;
  /** Raw `engine_pass` of the pass; null = the neutral fallback group. */
  pass: string | null;
  /** Visible label: the known pass name or the neutral "Engine" fallback. */
  label: string;
  /** Row tooltip: `<Label> · <State word>` (unknown non-null passes append the raw name). */
  tooltip: string;
  /** Right-rail state icon ligature. */
  railIcon: string;
  /** Right-rail icon classes: tone tint plus the spin animation while running. */
  railClass: string;
}

/** Resolved render model of one phase row: identity icon, description text and state rail. */
interface PhaseRowView {
  phase: BlackboardPhase;
  /** Visible row text: `PLAN_PHASE_INFO` label + its description, joined with an em dash. */
  description: string;
  /** Row tooltip: `<Label> · <State word>` — state words live only in tooltips. */
  tooltip: string;
  /** Phase identity icon ligature (`PLAN_PHASE_INFO.icon`). */
  icon: string;
  /** Right-rail state icon ligature. */
  railIcon: string;
  /** Right-rail icon classes: tone tint plus the spin animation while running. */
  railClass: string;
  /** True when the whole row renders dimmed (idle phase, row-level opacity). */
  idle: boolean;
}

/** One planned-task row of the pre-engine list: stable identity plus display intent. */
interface PlannedTaskRow {
  task_id: string;
  text: string;
}

/**
 * Plan steps as an array when well-formed; logs and returns an empty list on an unexpected
 * shape (never throws — stream-adjacent rendering must degrade, not break).
 */
function planStepsOf(plan: PlanData | null): PlanStep[] {
  const steps = plan?.steps ?? [];
  if (!Array.isArray(steps)) {
    console.error("plan-progress-card: unexpected plan steps shape", plan);
    return [];
  }
  return steps;
}

/**
 * Renders the live planning-pipeline progress (fluid-width card, replacement surface for the legacy
 * blackboard-card): eight phase rows of the doc 31 catalog with identity icons and a right-rail
 * state (live indicator first, durable `Plan.phases` records as fallback — a phase completed
 * persisted or live never renders gray again), one engine-pass sub-row per `engine_pass` seen
 * nested under the `run_engine` phase row (per-pass progress; the whole-engine row completes
 * only via the `plan_run`/`plan_close` signature or the durable record), a fixed two-line
 * detail slot (parked-task reason from the per-task pending details, then the live attempt
 * line, then plain engine liveness), the pre-engine planned-task list (placeholder "—" until
 * the planning `tasks` list arrives) and the grouped execution board (`app-plan-step-list`
 * without the confidence badge, states joined from the live task rows by `task_id`, falling
 * back to the durable `run_engine.task_executions`). Live state comes from the container-scoped
 * `BlackboardStreamService` (same optional-inject pattern as the legacy card); the durable plan
 * is the explicit `plan` input when the host carries one, else the plan-store join by the
 * streamed `plan_id` (doc 31 §7.5).
 */
@Component({
  selector: "app-plan-progress-card",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatTooltip, PlanStepListComponent],
  templateUrl: "./plan-progress-card.component.html",
  styleUrl: "./plan-progress-card.component.scss",
})
export class PlanProgressCardComponent {
  /**
   * Durable fallback plan (`plan` content block data) when the host carries one — explicit
   * input wins over the plan-store join. Null in live-weave contexts without a plan block;
   * the card then degrades to stream-only data.
   */
  readonly plan = input<PlanData | null>(null);

  /** Optional: same container-scoped planning stream the weaving `chat-message-list` reads. */
  readonly #blackboard = inject(BlackboardStreamService, { optional: true });

  /** Live frontend_tools interrupt — marks tasks the browser executes itself. */
  readonly #hitl = inject(HitlService, { optional: true });

  /**
   * Optional: durable plan-block store (doc 31 §7.5 join anchor) for the fallback plan when
   * the host passes no `plan` input. Provided per chat-container; null elsewhere (tests,
   * debug tooling) — the card then uses the stream data only.
   */
  readonly #planStore = inject(PlanBlockStoreService, { optional: true });

  /** Live phase indicators keyed by phase name (empty without a container stream). */
  readonly #liveIndicators = computed<Partial<Record<BlackboardPhase, BlackboardIndicator>>>(
    () => this.#blackboard?.indicators() ?? EMPTY_INDICATORS,
  );

  /** Live per-task engine rows as an array (empty without a container stream). */
  readonly #liveRows = computed<BlackboardTaskRow[]>(() => Array.from(this.#blackboard?.taskRows()?.values() ?? []));

  /** Live per-task engine rows keyed by task_id (state joins and attempt-line liveness). */
  readonly #liveRowsById = computed<ReadonlyMap<string, BlackboardTaskRow>>(() => {
    const byId = new Map<string, BlackboardTaskRow>();
    for (const row of this.#liveRows()) byId.set(row.task_id, row);
    return byId;
  });

  /** Live per-pass engine state from ENGINE-level `run_engine` events (engine-pass contract). */
  readonly #liveEnginePasses = computed<ReadonlyMap<string, BlackboardEnginePassState>>(
    () => this.#blackboard?.enginePasses() ?? EMPTY_PASS_MAP,
  );

  /** LIVE planning-progress intents (SSE v2 `tasks` list), keyed by task_id. */
  readonly #livePlanningTasks = computed<ReadonlyMap<string, string>>(
    () => this.#blackboard?.planningTasks() ?? EMPTY_INTENT_MAP,
  );

  /** Streamed task descriptions from attempt-start events, keyed by task_id (label fallback). */
  readonly #liveTaskDescriptions = computed<ReadonlyMap<string, string>>(
    () => this.#blackboard?.taskDescriptions() ?? EMPTY_INTENT_MAP,
  );

  /** Per-task pending park details (SSE v2, B1), keyed by task_id in event recency order. */
  readonly #taskPendingDetails = computed<ReadonlyMap<string, string>>(
    () => this.#blackboard?.taskPendingDetails() ?? EMPTY_INTENT_MAP,
  );

  /**
   * True when the engine run failed: the durable `run_engine` record or the catastrophic
   * `runError` from the last full snapshot. Failure wins over every other engine row state.
   */
  readonly #engineFailed = computed<boolean>(() => {
    if (this.#effectivePlan()?.phases?.run_engine?.status === "failed") return true;
    return !!this.#blackboard?.runError();
  });

  /**
   * True once the WHOLE engine finished: the durable whole-engine signature (`plan_run`
   * completed with step `plan_close` — plan_close.py:87,110) or the durable `run_engine`
   * record completed. Per-pass `run_engine/completed` events NEVER set this — they close
   * only their own pass row.
   */
  readonly #engineClosed = computed<boolean>(() => {
    const planRun = this.#liveIndicators().plan_run;
    if (planRun?.status === "completed" && planRun.step === "plan_close") return true;
    return this.#effectivePlan()?.phases?.run_engine?.status === "completed";
  });

  /**
   * True when the board is parked (status contract v3): no task is in the transient `running`
   * state and at least one parked signal exists — a live row in the collapsed `WAITING` or a
   * raw dump park variant (`WAITING_USER`/`WAITING_INPUT`), or a recorded park reason. Bare
   * `PENDING` rows alone (undelivered tasks of an active run) never park the engine.
   */
  readonly #engineParked = computed<boolean>(() => {
    const rows = this.#liveRows();
    if (rows.length === 0) return false;
    if (rows.some((row) => row.state === "running")) return false;
    if (this.#taskPendingDetails().size > 0) return true;
    return rows.some((row) => row.state === "WAITING" || row.state === "WAITING_USER" || row.state === "WAITING_INPUT");
  });

  /**
   * The durable fallback plan: the explicit `plan` input when provided, otherwise the
   * plan-store join by the streamed `plan_id` (doc 31 §7.5). Null while neither exists.
   */
  readonly #effectivePlan = computed<PlanData | null>(() => {
    const injected = this.plan();
    if (injected) return injected;
    const planId = this.#blackboard?.planId() ?? "";
    const planStore = this.#planStore;
    if (!planId || !planStore) return null;
    return planStore.byPlanId(planId) ?? null;
  });

  /** True once the engine has any execution data (live rows or the durable run record). */
  readonly #executionStarted = computed<boolean>(() => {
    if (this.#liveRows().length > 0) return true;
    return (this.#effectivePlan()?.phases?.run_engine?.task_executions?.length ?? 0) > 0;
  });

  /**
   * Grouped execution board for `app-plan-step-list` (plan-cards-spec §Plan progress 5):
   * steps come from the durable plan's `steps` (structural source); every task's state joins
   * the live row by `task_id` first, then the durable `run_engine.task_executions`, else
   * PENDING; the transient lowercase `running` maps to READY. Empty when the engine has no
   * execution data yet (the planned-task list covers the pre-engine view) or the plan carries
   * no steps with tasks.
   */
  protected readonly boardSteps = computed<PlanStepListStep[]>(() => {
    if (!this.#executionStarted()) return [];
    const steps = planStepsOf(this.#effectivePlan());
    if (steps.length === 0) return [];

    const plan = this.#effectivePlan();
    const rowsById = new Map<string, BlackboardTaskRow>(
      this.#liveRows().map((row): [string, BlackboardTaskRow] => [row.task_id, row]),
    );
    const executionsById = new Map<string, TaskExecution>(
      (plan?.phases?.run_engine?.task_executions ?? [])
        .filter((task) => task && typeof task.task_id === "string")
        .map((task): [string, TaskExecution] => [task.task_id, task]),
    );
    const liveFrontendIds = new Set(frontendToolTaskIds(this.#hitl?.frontendToolsPayload()));
    const planning = this.#livePlanningTasks();
    const descriptions = this.#liveTaskDescriptions();

    const labelOf = (taskId: string, intent: string | null): string =>
      (intent && intent.length > 0 ? intent : null) ?? planning.get(taskId) ?? descriptions.get(taskId) ?? taskId;

    const stateOf = (taskId: string): PlanTaskState => {
      const row = rowsById.get(taskId);
      if (row) return row.state === "running" ? "READY" : row.state;
      return executionsById.get(taskId)?.state ?? "PENDING";
    };

    const attemptsOf = (taskId: string): number | null =>
      rowsById.get(taskId)?.attempts ?? executionsById.get(taskId)?.attempts.length ?? null;

    return steps
      .filter((step) => step && Array.isArray(step.tasks) && step.tasks.length > 0)
      .map((step) => ({
        text: typeof step.text === "string" ? step.text : "",
        meta: null,
        confidence: null,
        tasks: step.tasks
          .filter((task) => task && typeof task.task_id === "string")
          .map((task) => ({
            text: labelOf(task.task_id, typeof task.intent === "string" ? task.intent : null),
            state: stateOf(task.task_id),
            attempts: attemptsOf(task.task_id),
            frontendTool:
              liveFrontendIds.has(task.task_id) ||
              frontendToolTaskIds(
                executionsById.get(task.task_id)?.diagnostics?.pending_reason?.interrupt_payload,
              ).includes(task.task_id),
          })),
      }));
  });

  /** Planned-task source when the board is empty: LIVE intents first, else plan step intents. */
  protected readonly plannedRows = computed<PlannedTaskRow[]>(() => {
    if (this.boardSteps().length > 0) return [];
    const live = Array.from(this.#livePlanningTasks(), ([taskId, text]) => ({ task_id: taskId, text }));
    if (live.length > 0) return live;
    const rows: PlannedTaskRow[] = [];
    for (const step of planStepsOf(this.#effectivePlan())) {
      for (const task of step?.tasks ?? []) {
        if (
          task &&
          typeof task.task_id === "string" &&
          typeof task.intent === "string" &&
          task.intent.length > 0 &&
          !rows.some((row) => row.task_id === task.task_id)
        ) {
          rows.push({ task_id: task.task_id, text: task.intent });
        }
      }
    }
    return rows;
  });

  /**
   * Compact "n/m tasks done" header counter — DONE tasks over the live rows, or over the
   * durable `run_engine.task_executions` when no live rows exist. Null while neither source
   * knows any tasks (the counter hides instead of rendering "0/0").
   */
  protected readonly progressText = computed<string | null>(() => {
    const rows = this.#liveRows();
    if (rows.length > 0) {
      return `${rows.filter((row) => row.state === "DONE").length}/${rows.length} tasks done`;
    }
    const executions = this.#effectivePlan()?.phases?.run_engine?.task_executions ?? [];
    if (executions.length > 0) {
      return `${executions.filter((task) => task && task.state === "DONE").length}/${executions.length} tasks done`;
    }
    return null;
  });

  /** All 8 catalog phases as display rows, in pipeline order. */
  protected readonly phaseRows = computed<PhaseRowView[]>(() => PHASE_ORDER.map((phase) => this.#phaseRow(phase)));

  /**
   * Fixed two-line detail slot text (plan-cards-spec §Plan progress 3): the most recent
   * per-task pending park reason wins ("Waiting: <detail>" — `PENDING`, `WAITING_INPUT` and
   * collapsed `waiting` parks alike), then the live attempt-start line ("Running '<task>' via <resolver>
   * (attempt N)"), then "Engine working…" while the engine is live. The attempt line never
   * falls back to the liveness line while its data exists: it reads `runAttempt` first and,
   * when a full snapshot reset it, persists from the kept per-task attempt metadata of the
   * newest task still in the transient `running` state. Null leaves the permanently-rendered
   * slot empty; the slot height never changes.
   */
  protected readonly detailText = computed<string | null>(() => {
    const pending = [...this.#taskPendingDetails().values()];
    if (pending.length > 0) return `Waiting: ${pending[pending.length - 1]}`;
    const line = this.#attemptLine();
    if (line) return line;
    return this.#engineRowState() === "running" ? "Engine working…" : null;
  });

  /**
   * Formats one attempt line — full data renders `Running '<task_description>' via
   * <resolver_id> (attempt N)`; missing parts drop out instead of degrading the line.
   */
  #attemptLineText(description: string | null, resolverId: string | null, attempt: number): string {
    const quoted = description ? ` '${description}'` : "";
    const via = resolverId ? ` via ${resolverId}` : "";
    return `Running${quoted}${via} (attempt ${attempt})`;
  }

  /**
   * The live attempt line (SSE v2): the streamed `runAttempt` while its task row is still in
   * the transient `running` state, else the newest kept per-task attempt metadata whose task
   * row still runs (the map is recency-ordered — newest attempt-start is the LAST entry). The
   * fallback persists the line across full-snapshot resets without ever showing a finished
   * attempt; when no running task carries attempt data the line drops to the liveness slot.
   */
  #attemptLine(): string | null {
    const rows = this.#liveRowsById();
    const attempt = this.#blackboard?.runAttempt() ?? null;
    if (attempt && rows.get(attempt.task_id)?.state === "running") {
      return this.#attemptLineText(attempt.description, attempt.resolver_id, attempt.attempt);
    }
    const attempts = this.#blackboard?.taskAttempts() ?? EMPTY_ATTEMPT_MAP;
    for (const [taskId, info] of [...attempts.entries()].reverse()) {
      if (rows.get(taskId)?.state === "running") {
        return this.#attemptLineText(info.taskDescription, info.resolverId, info.attempt);
      }
    }
    return null;
  }

  /** Resolved render model for one phase row (state semantics differ for run_engine). */
  #phaseRow(phase: BlackboardPhase): PhaseRowView {
    const info = PLAN_PHASE_INFO[phase];
    const state = phase === "run_engine" ? this.#engineRowState() : this.#genericPhaseRowState(phase);
    const rail = PHASE_RAIL_COPY[state];
    return {
      phase,
      description: `${info.label} — ${info.meaning}`,
      tooltip: `${info.label} · ${rail.word}`,
      icon: info.icon,
      railIcon: rail.icon,
      railClass: state === "running" ? `${rail.toneClass} plan-progress-card__spin` : rail.toneClass,
      idle: state === "idle",
    };
  }

  /**
   * Row state for a regular phase. Precedence: the live motion wins (a phase may re-run after
   * being completed on a replan round), then the live terminal statuses, then the durable
   * fallback — a phase completed persisted or live never renders gray again (a missing or
   * unknown live indicator falls back to the derived completion instead of resetting to idle).
   */
  #genericPhaseRowState(phase: BlackboardPhase): PhaseRowState {
    const status = this.#liveIndicators()[phase]?.status;
    if (status === "running") return "running";
    if (status === "completed") return "completed";
    if (status === "error") return "failed";
    if (status != null) console.error("plan-progress-card: unexpected phase status", phase, status);
    return this.#planDerivedPhaseCompleted(phase) ? "completed" : "idle";
  }

  /**
   * Row state for `run_engine`. Its indicator `status` is the raw word of the LAST event of
   * either level (doc 31 §7.3 + engine-pass contract) — never trusted directly. Precedence:
   * failure wins (durable record or catastrophic `runError`); a live attempt whose row is
   * still `running` wins over everything below (a park on one task cannot revoke a peer's
   * in-flight attempt); whole-engine completion comes ONLY from the `plan_run`/`plan_close`
   * completed signature or the durable record — a per-pass `run_engine/completed` closes only
   * its own pass sub-row, NEVER this row, and board idleness (no READY rows) completes
   * nothing; a parked board (status contract v3) pauses the row — the engine cannot progress
   * until the park is resolved, so it must not spin indefinitely; an engine that started but
   * has neither closed nor parked is running (wrapping up between passes counts as live);
   * before the engine ever starts the row is idle.
   */
  #engineRowState(): PhaseRowState {
    if (this.#engineFailed()) return "failed";
    const attempt = this.#blackboard?.runAttempt() ?? null;
    if (attempt != null && this.#liveRowsById().get(attempt.task_id)?.state === "running") {
      return "running";
    }
    const passMotion = [...this.#liveEnginePasses().values()].some((pass) => pass.status === "running");
    // Pass motion above a stale closed signature keeps re-run liveness (doc ruling) — but a
    // parked board never resumes the spin from stale pass entries.
    if (passMotion && !this.#engineParked()) return "running";
    if (this.#engineClosed()) return "completed";
    if (this.#engineParked()) return "waiting";
    if (passMotion) return "running";
    if (this.#liveIndicators().run_engine || this.#effectivePlan()?.phases?.run_engine?.status === "in_progress") {
      return "running";
    }
    return "idle";
  }

  /**
   * All seen engine passes as display sub-rows (engine-pass contract): canonical known order
   * first, then unknown first-seen passes, then the neutral fallback group last. Rows exist
   * only for passes the stream actually named — never pre-created for passes never seen.
   */
  protected readonly enginePassRows = computed<EnginePassRowView[]>(() => {
    const passes = this.#liveEnginePasses();
    if (passes.size === 0) return [];
    const engineState = this.#engineRowState();
    const rows: EnginePassRowView[] = [];
    for (const pass of ENGINE_PASS_ORDER) {
      const state = passes.get(pass);
      if (state) rows.push(this.#enginePassRowView(pass, state, engineState));
    }
    let fallback: { key: string; state: BlackboardEnginePassState } | null = null;
    for (const [key, state] of passes) {
      if (state.pass == null) {
        fallback = { key, state };
        continue;
      }
      if (!ENGINE_PASS_ORDER.includes(state.pass)) {
        rows.push(this.#enginePassRowView(key, state, engineState));
      }
    }
    if (fallback) rows.push(this.#enginePassRowView(fallback.key, fallback.state, engineState));
    return rows;
  });

  /**
   * Resolved render model of one engine-pass sub-row. Status vocabulary: lowercase
   * `running`/`completed` from engine-level events, `error` tolerated as failed, any unknown
   * future string logged and treated as running. Terminal engine motion overrides unfinished
   * rows: a failed engine interrupts its still-running passes, a parked engine pauses them
   * (the running entries are stale liveness — nothing can progress while parked), and the
   * neutral fallback group (heartbeat-derived, no per-pass completed of its own) closes with
   * the whole engine — a pass completed by its own event never changes.
   */
  #enginePassRowView(key: string, state: BlackboardEnginePassState, engineState: PhaseRowState): EnginePassRowView {
    const known = state.pass != null && ENGINE_PASS_LABELS[state.pass] != null;
    const label = state.pass == null ? "Engine" : ENGINE_PASS_LABELS[state.pass] ?? "Engine";
    const rawSuffix = known || state.pass == null ? "" : ` (${state.pass})`;
    let rowState: PhaseRowState;
    if (state.status === "running") rowState = "running";
    else if (state.status === "completed") rowState = "completed";
    else if (state.status === "error") rowState = "failed";
    else {
      console.error("plan-progress-card: unexpected engine pass status", state.pass, state.status);
      rowState = "running";
    }
    if (rowState !== "completed") {
      if (engineState === "failed") rowState = "failed";
      else if (engineState === "waiting" && rowState === "running") rowState = "waiting";
      else if (state.pass == null && engineState === "completed") rowState = "completed";
    }
    const rail = PHASE_RAIL_COPY[rowState];
    return {
      key,
      pass: state.pass,
      label,
      tooltip: `${label}${rawSuffix} · ${rail.word}`,
      railIcon: rail.icon,
      railClass: rowState === "running" ? `${rail.toneClass} plan-progress-card__spin` : rail.toneClass,
    };
  }

  /**
   * Durable completion for one derivable phase (plan.phases records fallback): the matching
   * `Plan.phases` record status for the F1-F3 and assessment phases, and `plan.state` for the
   * router/triage/run-prep trio (approved → triage, executed → triage + run prep + router, closed on
   * every success route). `run_engine` keeps its own live semantics and is never derived. No
   * plan, an early plan without phase records, or a non-completed record → false (stays idle).
   */
  #planDerivedPhaseCompleted(phase: BlackboardPhase): boolean {
    const plan = this.#effectivePlan();
    const phases = plan?.phases;
    if (!phases) return false;
    switch (phase) {
      case "semantic_decomposition":
      case "capability_resolution":
      case "provider_binding":
        return phases[phase]?.status === "completed";
      case "plan_assessment":
        return phases.plan_assessment?.status === "completed";
      case "plan_triage":
        return plan.state === "approved" || plan.state === "executed";
      case "plan_run":
      case "plan_router":
        return plan.state === "executed";
      case "run_engine":
        return false;
    }
  }
}
